import { MAX_MEDIA_BYTES } from './zionMediaProtocol.ts'

const MIN_VALID_AUDIO_BYTES = 100

export interface EncodedVoice {
  bytes: Uint8Array
  mime: string
  durationMs: number
}

/**
 * Converte WebM/Ogg (PWA) in WAV PCM 16-bit mono per Android.
 * Se la conversione fallisce o produce byte invalidi → fallback WebM originale.
 */
export async function encodeVoiceForAndroid(
  input: Uint8Array,
  mime: string,
  durationMsHint = 0,
): Promise<EncodedVoice> {
  const originalMime = normalizeWireMime(mime) || 'audio/webm'
  const fallback = (): EncodedVoice => ({
    bytes: input,
    mime: originalMime.startsWith('audio/') ? originalMime : 'audio/webm',
    durationMs: Math.max(0, durationMsHint),
  })

  if (!input.length || input.length < MIN_VALID_AUDIO_BYTES) {
    return fallback()
  }

  if (isWavBytes(input) || originalMime.includes('wav')) {
    if (isValidWavPcm16Mono(input)) {
      const dur = durationMsFromWav(input) || durationMsHint
      return { bytes: input, mime: 'audio/wav', durationMs: dur }
    }
    // Header WAV corrotto → non rischiare invio "WAV" fake
    return fallback()
  }

  // mp4/m4a nativo: già ok per Android
  if (
    originalMime.includes('mp4') ||
    originalMime.includes('m4a') ||
    originalMime.includes('aac')
  ) {
    return { bytes: input, mime: originalMime, durationMs: Math.max(0, durationMsHint) }
  }

  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new AudioCtx()
    const ab = new ArrayBuffer(input.byteLength)
    new Uint8Array(ab).set(input)
    const decoded = await ctx.decodeAudioData(ab)
    await ctx.close().catch(() => undefined)

    const durationMs = Math.round((decoded.duration || 0) * 1000) || durationMsHint
    if (durationMs <= 0 || decoded.length <= 0) return fallback()

    const rates = [16_000, 12_000, 8_000]
    for (const rate of rates) {
      const pcm = downsampleToMono(decoded, rate)
      if (pcm.length < 16) continue
      const wav = encodeWavPcm16(pcm, rate)
      if (
        wav.length >= MIN_VALID_AUDIO_BYTES &&
        wav.length <= MAX_MEDIA_BYTES &&
        isValidWavPcm16Mono(wav)
      ) {
        return { bytes: wav, mime: 'audio/wav', durationMs }
      }
    }

    // Ultimo tentativo: taglia a 8 kHz sotto il limite
    const pcm8 = downsampleToMono(decoded, 8_000)
    const maxSamples = Math.floor((MAX_MEDIA_BYTES - 44) / 2)
    const trimmed = pcm8.length > maxSamples ? pcm8.subarray(0, maxSamples) : pcm8
    if (trimmed.length < 16) return fallback()
    const wav = encodeWavPcm16(trimmed, 8_000)
    if (wav.length >= MIN_VALID_AUDIO_BYTES && isValidWavPcm16Mono(wav)) {
      return {
        bytes: wav,
        mime: 'audio/wav',
        durationMs: Math.round((trimmed.length / 8_000) * 1000) || durationMs,
      }
    }
    return fallback()
  } catch {
    return fallback()
  }
}

function normalizeWireMime(mime: string): string {
  const m = mime.trim().toLowerCase()
  if (!m) return ''
  // Mantieni codecs per WebM se presente (utile ExoPlayer)
  if (m.startsWith('audio/webm')) {
    return m.includes('opus') ? 'audio/webm;codecs=opus' : 'audio/webm'
  }
  return m.split(';')[0]!.trim()
}

function isWavBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x41 &&
    bytes[10] === 0x56 &&
    bytes[11] === 0x45
  )
}

/** Controlla RIFF/WAVE + fmt PCM 16-bit mono + chunk data. */
export function isValidWavPcm16Mono(bytes: Uint8Array): boolean {
  if (bytes.length < 44) return false
  if (!isWavBytes(bytes)) return false
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  // "fmt "
  if (
    bytes[12] !== 0x66 ||
    bytes[13] !== 0x6d ||
    bytes[14] !== 0x74 ||
    bytes[15] !== 0x20
  ) {
    return false
  }
  const audioFormat = view.getUint16(20, true)
  const numChannels = view.getUint16(22, true)
  const sampleRate = view.getUint32(24, true)
  const bitsPerSample = view.getUint16(34, true)
  if (audioFormat !== 1) return false // PCM
  if (numChannels !== 1) return false
  if (bitsPerSample !== 16) return false
  if (sampleRate < 8000 || sampleRate > 48000) return false
  // "data" a offset 36 (header standard 44 byte)
  if (
    bytes[36] !== 0x64 ||
    bytes[37] !== 0x61 ||
    bytes[38] !== 0x74 ||
    bytes[39] !== 0x61
  ) {
    return false
  }
  const dataSize = view.getUint32(40, true)
  if (dataSize <= 0 || dataSize % 2 !== 0) return false
  if (44 + dataSize > bytes.length) return false
  return true
}

function durationMsFromWav(bytes: Uint8Array): number {
  if (!isValidWavPcm16Mono(bytes)) return 0
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const sampleRate = view.getUint32(24, true)
  const dataSize = view.getUint32(40, true)
  if (sampleRate <= 0) return 0
  return Math.round((dataSize / 2 / sampleRate) * 1000)
}

function downsampleToMono(buffer: AudioBuffer, targetRate: number): Int16Array {
  const channels = buffer.numberOfChannels
  const srcRate = buffer.sampleRate
  const length = buffer.length
  const mono = new Float32Array(length)
  for (let ch = 0; ch < channels; ch++) {
    const data = buffer.getChannelData(ch)
    for (let i = 0; i < length; i++) mono[i]! += data[i]! / channels
  }

  if (srcRate === targetRate) {
    const out = new Int16Array(length)
    for (let i = 0; i < length; i++) out[i] = floatToInt16(mono[i]!)
    return out
  }

  const ratio = srcRate / targetRate
  const outLen = Math.max(1, Math.floor(length / ratio))
  const out = new Int16Array(outLen)
  for (let i = 0; i < outLen; i++) {
    const srcIndex = Math.min(length - 1, Math.floor(i * ratio))
    out[i] = floatToInt16(mono[srcIndex]!)
  }
  return out
}

function floatToInt16(sample: number): number {
  const s = Math.max(-1, Math.min(1, sample))
  return s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff)
}

/** Header WAV standard 44 byte — PCM 16-bit LE mono (scrittura fisica byte-per-byte). */
function encodeWavPcm16(samples: Int16Array, sampleRate: number): Uint8Array {
  const dataSize = samples.length * 2
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  /* RIFF identifier */
  writeString(view, 0, 'RIFF')
  /* file length minus 8 */
  view.setUint32(4, 36 + dataSize, true)
  /* RIFF type */
  writeString(view, 8, 'WAVE')
  /* format chunk identifier */
  writeString(view, 12, 'fmt ')
  /* format chunk length */
  view.setUint32(16, 16, true)
  /* sample format (raw PCM) */
  view.setUint16(20, 1, true)
  /* channel count */
  view.setUint16(22, 1, true)
  /* sample rate */
  view.setUint32(24, sampleRate, true)
  /* byte rate (sample rate * block align) */
  view.setUint32(28, sampleRate * 2, true)
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, 2, true)
  /* bits per sample */
  view.setUint16(34, 16, true)
  /* data chunk identifier */
  writeString(view, 36, 'data')
  /* data chunk length */
  view.setUint32(40, dataSize, true)

  /* Write PCM samples (16-bit LE) */
  let offset = 44
  for (let i = 0; i < samples.length; i++, offset += 2) {
    view.setInt16(offset, samples[i]!, true)
  }

  // Copia su buffer fresco + assert header non nullo (evita invio 44 zero)
  const out = new Uint8Array(buffer.byteLength)
  out.set(new Uint8Array(buffer))
  if (
    out[0] !== 0x52 ||
    out[1] !== 0x49 ||
    out[2] !== 0x46 ||
    out[3] !== 0x46 ||
    out[8] !== 0x57 ||
    out[9] !== 0x41 ||
    out[10] !== 0x56 ||
    out[11] !== 0x45
  ) {
    throw new Error('WAV header non scritto (RIFF/WAVE mancanti)')
  }
  return out
}

function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i))
  }
}
